import React, { FC, useCallback, useEffect, useRef, useState, RefObject, useMemo } from 'react';
import { createEditor, Descendant, Editor, Transforms } from 'slate';
import { withHistory } from 'slate-history';
import { Editable, Slate, withReact, ReactEditor } from 'slate-react';
import { useAppSelector } from '../../app/hooks';

// Your other plugins
import withLinks from './plugins/withLinks';
import withTables from './plugins/withTable';
import withEmbeds from './plugins/withEmbeds';
import withEquation from './plugins/withEquation';

// The new HTML paste plugin

import Toolbar from './Toolbar/Toolbar';
import { getBlock, getMarked, toggleMark } from './utils/SlateUtilityFunctions';
import TextEditorShimmerLoader from './TextEditorShimmerLoader';

import styles from './TextEditor.module.scss';
import withHtmlPaste from './utils/withHtmlPaste';
import { Mixpanel } from '../../utils/mixpanel';
import { getSubdomain } from '../../utils/utils';
import {
    AICA_BUREAU_KYC_CONSENT_EDIT,
    AICA_ENGAGEMENT_LETTER_EDIT,
    AICA_ONEPAGER_EDIT,
} from '../../utils/constants/mixpanelEvents/investorEvents';
import { TEXT_EDITOR_TYPES } from '../../store/investor/constants';
import { useEditorSync } from './useEditorSync';
import { resetAllSelections } from './Elements/Table/components/tableSelectionUtils';

// --------------------------------------
// Types
// --------------------------------------
interface SlateEditorProps {
    source: string;
    editorInitialData: Descendant[];
    editorContentRef: React.MutableRefObject<Descendant[]>;
    targetRef?: RefObject<HTMLDivElement>;
    assesseeOrgId?: string;
    generating?: boolean;
    onClose?: () => void;
    savingData: boolean;
    editorType: string;
}

// We extend the base Slate editor to include React/History types
// If you have custom node types, define them in a `CustomTypes` definition file
// and import them appropriately.
type CustomEditor = ReactEditor;

// Simple element component
const Element: FC<any> = (props) => {
    return getBlock(props);
};

// Simple leaf component
const Leaf: FC<any> = (props) => {
    let { attributes, children, leaf } = props;
    children = getMarked(leaf, children);
    return <span {...attributes}>{children}</span>;
};

const TextEditor: FC<SlateEditorProps> = ({
    source,
    editorContentRef,
    editorInitialData,
    targetRef,
    assesseeOrgId,
    generating,
    onClose,
    savingData,
    editorType,
}) => {
    const user = useAppSelector((state) => state.user.userData);
    // --------------------------------------
    // Create the editor with plugins
    // --------------------------------------
    const editor = useMemo(
        () => withEquation(withHistory(withEmbeds(withTables(withHtmlPaste(withLinks(withReact(createEditor()) as CustomEditor)))))),
        [],
    );
    const isFirstKeyStrokeRef = useRef(false);
    useEditorSync(editor, editorInitialData);

    // Initialize editorContentRef with editorInitialData if it's currently empty.
    useEffect(() => {
        if (editorInitialData?.length && editorContentRef?.current?.length === 0) {
            editorContentRef.current = editorInitialData;
        }
    }, [editorInitialData, editorContentRef]);

    // --------------------------------------
    // Handle changes to editor content
    // --------------------------------------
    const handleEditorChange = useCallback(
        (value: Descendant[]) => {
            // Detect actual changes
            const isAstChange = editor.operations.some((op) => op.type !== 'set_selection');
            if (isAstChange && source === 'CREATE_DEAL') {
                // Optionally store new content in localStorage
                // const content = JSON.stringify(value);
                // localStorage.setItem('one_pager_content', content);
                // localStorage.setItem('one_pager_assessee', assesseeOrgId || '');
            }
            editorContentRef.current = value;

            // calculatePageBreaks();
        },
        [editor, source, editorContentRef, assesseeOrgId],
    );

    const getMixpanelEditor = (editorType: string) => {
        switch (editorType) {
            case TEXT_EDITOR_TYPES.ONE_PAGER:
                return AICA_ONEPAGER_EDIT;
            case TEXT_EDITOR_TYPES.ENGAGEMENT_LETTER:
                return AICA_ENGAGEMENT_LETTER_EDIT;
            case TEXT_EDITOR_TYPES.BUREAU_KYC_CONSENT:
                return AICA_BUREAU_KYC_CONSENT_EDIT;
            default:
                return '';
        }
    };

    // --------------------------------------
    // Keyboard shortcuts
    // --------------------------------------
    const handleKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLDivElement>) => {
            // Typically Mac's "Command" key is "metaKey" and Windows' Control is `ctrlKey`.
            // Adjust if you want cross-platform behavior, e.g. using event.ctrlKey instead.
            if (event.metaKey) {
                switch (event.key) {
                    case 'b': {
                        event.preventDefault();
                        toggleMark(editor, 'bold');
                        break;
                    }
                    case 'i': {
                        event.preventDefault();
                        toggleMark(editor, 'italic');
                        break;
                    }
                    case 'u': {
                        event.preventDefault();
                        toggleMark(editor, 'underline');
                        break;
                    }
                    case 's': {
                        event.preventDefault();
                        toggleMark(editor, 'strikethrough');
                        break;
                    }
                    default:
                        break;
                }
            }

            if (!isFirstKeyStrokeRef.current) {
                isFirstKeyStrokeRef.current = true;
                const mixpanelEditor = getMixpanelEditor(editorType);

                {
                    mixpanelEditor &&
                        Mixpanel.track(mixpanelEditor, {
                            userRole: user?.accountType,
                            pnoPlatform: getSubdomain(),
                            userId: user?.userId,
                            currentUrl: window.location.href,
                            organizationId: assesseeOrgId,
                        });
                }
            }
        },
        [editor, editorType, user, assesseeOrgId],
    );

    // --------------------------------------
    // Render functions
    // --------------------------------------
    const renderElement = useCallback((props: any) => <Element {...props} />, []);
    const renderLeaf = useCallback((props: any) => {
        const { attributes, children, leaf } = props;

        let style: any = {};

        // Apply font family if specified
        if (leaf.fontFamily) {
            style.fontFamily = leaf.fontFamily;
        }

        return <span {...attributes}>{getMarked(leaf, children)}</span>;
    }, []);

    // --------------------------------------
    // Example HTML code -> text injection (if you have that feature)
    // --------------------------------------
    const [htmlAction, setHtmlAction] = useState<{
        showInput: boolean;
        html: string;
        action: string;
        location: string;
    }>({
        showInput: false,
        html: '',
        action: '',
        location: '',
    });

    const handleCodeToText = (partialState: Partial<typeof htmlAction>) => {
        setHtmlAction((prev) => ({
            ...prev,
            ...partialState,
        }));
    };

    const handleEditorClick = useCallback(
        (event) => {
            if (!event.target.closest('[data-column-selector]') && !event.target.closest('[data-row-selector]')) {
                resetAllSelections(editor);
            }
        },
        [editor],
    );

    return (
        <div className={styles.SlateWrapper}>
            <Slate editor={editor} initialValue={editorInitialData} onChange={handleEditorChange}>
                <Toolbar handleCodeToText={handleCodeToText} onClose={onClose} savingData={savingData} />
                {generating ? (
                    <TextEditorShimmerLoader />
                ) : (
                    <div className={styles.EditorWrapper} ref={targetRef} id="element-to-be-downloaded-as-pdf">
                        <Editable
                            placeholder="Write your text here..."
                            renderElement={renderElement}
                            renderLeaf={renderLeaf}
                            onKeyDown={handleKeyDown}
                            spellCheck={true}
                            onClick={handleEditorClick}
                        />
                    </div>
                )}
            </Slate>
        </div>
    );
};

export default TextEditor;
